import * as React from 'react'
import isFunction from 'lodash/isFunction'
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState, TableOptions } from '@tanstack/react-table'

import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@klear/ui/components/table'

import { DataTablePagination } from '@klear/ui/components/data-table-pagination'
import { DataTableToolbar } from '@klear/ui/components/data-table-toolbar'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@klear/ui/lib/utils'

export interface DataTableFilter {
  title: string
  column: string
  options: {
    label: string
    value: string | boolean
    icon?: LucideIcon
  }[]
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  filters?: DataTableFilter[]
  data: TData[]
  onRowClick?: (row: TData) => void
  showFilters?: boolean
  showPagination?: boolean
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (visibility: VisibilityState) => void
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void
  initialSorting?: SortingState
}

export function DataTable<TData, TValue>({
  columns,
  filters = [],
  data,
  onRowClick,
  loading,
  showFilters = true,
  showPagination = true,
  columnVisibility,
  onColumnVisibilityChange,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange,
  initialSorting = []
}: DataTableProps<TData, TValue> & { loading?: boolean }) {
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibilityState, setColumnVisibilityState] = React.useState<VisibilityState>(columnVisibility || {})
  const [columnFiltersState, setColumnFiltersState] = React.useState<ColumnFiltersState>(externalColumnFilters || [])
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting)

  const columnFilters = externalColumnFilters ?? columnFiltersState

  const tableOptions: TableOptions<TData> = {
    data: data ?? [],
    columns,
    initialState: {
      pagination: {
        pageSize: 10
      }
    },
    state: {
      sorting,
      columnVisibility: columnVisibility || columnVisibilityState,
      rowSelection,
      columnFilters
    },
    enableRowSelection: true,
    enableGlobalFilter: showFilters,
    globalFilterFn: 'includesString',
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
      const newFilters = isFunction(updater) ? updater(columnFilters) : updater
      onColumnFiltersChange?.(newFilters)
      setColumnFiltersState(newFilters)
    },
    onColumnVisibilityChange: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
      const newState = isFunction(updater) ? updater(columnVisibility || columnVisibilityState) : updater
      onColumnVisibilityChange?.(newState)
      setColumnVisibilityState(newState)
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues()
  }

  const table = useReactTable(tableOptions)

  return (
    <div className="space-y-4">
      {showFilters && <DataTableToolbar table={table} filters={filters} />}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan} className={cn('text-left')}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  className={onRowClick ? 'cursor-pointer hover:bg-muted' : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="text-left">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showPagination && <DataTablePagination table={table} />}
    </div>
  )
}
